import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Confetti from 'react-confetti';
import Layout from '@/components/Layout';
import VerifiedBadge from '@/components/VerifiedBadge';
import ProgressRing from '@/components/ProgressRing';
import PollPopup from '@/components/PollPopup';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Target, Flame, Zap, TrendingUp, Clock, Video, BookOpen, Award, Calendar, ArrowUpRight, Play, Trophy, Download, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function Dashboard({ user, onLogout, onUpdateUser }) {
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [reportEligible, setReportEligible] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    autoCheckChallenges();
    checkReportEligibility();
    
    const shouldShowConfetti = sessionStorage.getItem('show_login_confetti');
    if (shouldShowConfetti === 'true') {
      setShowConfetti(true);
      sessionStorage.removeItem('show_login_confetti');
      setTimeout(() => setShowConfetti(false), 5000);
    }

    // Show poll popup after dashboard loads
    setTimeout(() => {
      setShowPoll(true);
    }, 1500);
  }, []);

  const autoCheckChallenges = async () => {
    try {
      const res = await axios.post(`${API}/api/student-challenges/auto-check-all`, {}, { headers: getAuthHeaders() });
      
      if (res.data.newly_completed && res.data.newly_completed.length > 0) {
        console.log(`✅ Auto-completed ${res.data.count} challenges:`, res.data.newly_completed);
        
        // Refresh dashboard data to show updated challenges
        fetchDashboardData();
        
        // Show celebration confetti
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
        
        // You could also show a toast notification here
        const totalGP = res.data.newly_completed.reduce((sum, c) => sum + c.gp_reward, 0);
        console.log(`🏆 You earned ${totalGP} GP from ${res.data.count} completed challenges!`);
      }
    } catch (error) {
      console.error('Failed to auto-check challenges:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // OPTIMIZED: Use my-courses endpoint for better performance
      // ALSO fetch fresh user data to get updated streak
      const [userRes, statsRes, coursesRes, announcementsRes, liveClassesRes, challengesRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),  // Fetch fresh user data
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/my-courses`, { headers: getAuthHeaders() }),  // Changed to optimized endpoint
        axios.get(`${API}/announcements`, { headers: getAuthHeaders() }),
        axios.get(`${API}/live-classes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/student-challenges`, { headers: getAuthHeaders() }),
        axios.get(`${API}/leaderboard/overall`, { headers: getAuthHeaders() })
      ]);

      // Update parent component with fresh user data (includes updated streak)
      if (onUpdateUser && userRes.data) {
        onUpdateUser(userRes.data);
      }

      setStats(statsRes.data);
      setCourses(coursesRes.data);
      setAnnouncements(announcementsRes.data.slice(0, 5));
      setChallenges(challengesRes.data.slice(0, 3)); // Top 3 challenges
      
      // Find current user's rank from leaderboard
      const currentUserEntry = leaderboardRes.data.find(entry => entry.user_id === user.id);
      setUserRank(currentUserEntry ? currentUserEntry.rank : null);
      
      const upcoming = liveClassesRes.data.filter(c => 
        c.status === 'scheduled' && new Date(c.scheduled_at) > new Date()
      );
      setLiveClasses(upcoming);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${BACKEND_URL}${photoPath}`;
  };

  const checkReportEligibility = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/student/${user.id}/eligibility`,
        { headers: getAuthHeaders() }
      );
      setReportEligible(response.data.eligible);
    } catch (error) {
      console.error('Error checking report eligibility:', error);
      setReportEligible(false);
    }
  };

  const downloadProgressReport = async () => {
    if (!reportEligible) {
      toast.error('Complete at least one course up to 80% to generate your official Mastery Report.');
      return;
    }

    try {
      setDownloadingReport(true);
      
      const response = await fetch(
        `${API}/reports/student/${user.id}/download`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('lms_token')}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${user.first_name}_${user.last_name}_STEM_Mastery_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error.message || 'Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="dashboard"><div className="p-8">Loading...</div></Layout>;
  }

  const nextClass = liveClasses[0];
  const todayCourse = courses[0];

  return (
    <Layout user={user} onLogout={onLogout} currentPage="dashboard">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
        />
      )}
      <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6" data-testid="student-dashboard">
        
        {/* Modern Hero Card */}
        <Card className="border-0 card-shadow bg-white rounded-2xl overflow-hidden">
          <CardContent className="p-4 md:p-6 lg:p-8">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 md:space-x-4 lg:space-x-6 flex-1">
                <div className="relative">
                  <Avatar className="w-16 h-16 md:w-20 md:h-20 lg:w-28 lg:h-28 border-2 md:border-4 border-white ring-2 md:ring-4 ring-[#E7E7E7]">
                    <AvatarImage src={getPhotoUrl(user.profile_photo)} />
                    <AvatarFallback className="text-lg md:text-2xl lg:text-3xl font-semibold bg-primary-blue text-white">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2 md:mb-4">
                    {/* Left side - Name and subtitle */}
                    <div>
                      <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-navy flex items-center space-x-2 mb-1">
                        <span>Namaskar, {user.first_name}!</span>
                        <VerifiedBadge show={user.profile_verified} size="lg" />
                      </h1>
                      <p className="text-sm md:text-[15px] text-[#4D4D4D] font-light mb-2 md:mb-4">A Proud Genius Idiot</p>
                      
                      {/* Streak and GP - Aligned under "Proud" (P) */}
                      <div className="space-y-1 md:space-y-2 pl-1">
                        <div className="flex items-center space-x-2">
                          <Flame className="w-4 h-4 md:w-5 md:h-5 text-[#FF8B3D]" strokeWidth={2} />
                          <span className="text-xs md:text-sm text-[#4D4D4D]">Streak:</span>
                          <span className="text-base md:text-lg font-bold text-navy">{user.streak_count || 0}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Zap className="w-4 h-4 md:w-5 md:h-5 text-primary-blue" strokeWidth={2} />
                          <span className="text-xs md:text-sm text-[#4D4D4D]">GP:</span>
                          <span className="text-base md:text-lg font-bold text-navy">{user.xp_points}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Date */}
                    <div className="text-right">
                      <p className="text-sm md:text-[15px] font-semibold text-[#4D4D4D]">
                        {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Horizontal Progress Bar */}
                  <div className="mt-4 md:mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs md:text-sm font-semibold text-navy">Course Progress</span>
                      <span className="text-base md:text-lg font-bold text-primary-blue">{stats?.average_course_progress?.toFixed(0) || 0}%</span>
                    </div>
                    <div className="w-full h-2 md:h-3 bg-[#E7E7E7] rounded-full overflow-hidden">
                      <div 
                        className="h-full premium-gradient rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${stats?.average_course_progress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Download Progress Report - Always visible */}
        <Card className={`border-0 card-shadow rounded-2xl overflow-hidden ${reportEligible ? 'bg-gradient-to-r from-[#16213e] to-[#0f3460]' : 'bg-gradient-to-r from-[#2d3748] to-[#4a5568]'}`} data-testid="report-download-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-bold text-white">
                    {reportEligible ? 'STEM Mastery Report Ready' : 'STEM Mastery Report'}
                  </h3>
                  <p className="text-xs md:text-sm text-white/70">
                    {reportEligible ? 'Download your certified progress report' : 'Complete any course to 80% to unlock your report'}
                  </p>
                </div>
              </div>
              <Button
                data-testid="download-report-btn"
                onClick={downloadProgressReport}
                disabled={downloadingReport}
                className={`font-semibold px-4 md:px-6 py-2 rounded-xl transition-smooth btn-press ${reportEligible ? 'bg-white text-[#16213e] hover:bg-white/90' : 'bg-white/20 text-white hover:bg-white/30'}`}
              >
                {downloadingReport ? (
                  <span className="flex items-center space-x-2">
                    <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></span>
                    <span>Generating...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Download Report</span>
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          
          {/* Course Progress */}
          <Card className="border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl cursor-pointer">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl premium-gradient flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide">Course Progress</p>
                  <p className="text-xl md:text-2xl font-bold text-navy">{stats?.average_course_progress?.toFixed(0) || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

            {/* Streak - Rolling 7-day window */}
            <Card className="border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl cursor-pointer">
              <CardContent className="p-4 md:p-5">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl streak-gradient flex items-center justify-center flex-shrink-0">
                    <Flame className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide">Streak</p>
                    <p className="text-xl md:text-2xl font-bold text-navy">{user.streak_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GDIOT Points */}
            <Card className="border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl cursor-pointer">
              <CardContent className="p-4 md:p-5">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl premium-gradient flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide">GDIOT Points</p>
                    <p className="text-xl md:text-2xl font-bold text-navy">{user.xp_points} <span className="text-sm md:text-base font-normal">GP</span></p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Live Classes */}
            <Card className="border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl cursor-pointer">
              <CardContent className="p-4 md:p-5">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl premium-gradient flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-[#A5A5A5] uppercase tracking-wide">Live Classes</p>
                    <p className="text-xl md:text-2xl font-bold text-navy">{liveClasses.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        {/* Learning & Classes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          
          {/* Today's Learning Goal */}
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold text-navy mb-3 md:mb-4">Today&apos;s Learning Goal</h3>
              {todayCourse ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#F7F7F7]">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 rounded-xl premium-gradient flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-white" strokeWidth={2} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-[15px] text-navy mb-1">{todayCourse.title}</h4>
                        <p className="text-sm text-[#4D4D4D] font-light">Continue your learning journey</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate(`/courses/${todayCourse.id}`)}
                    className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 rounded-xl transition-smooth btn-press"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Continue Learning
                  </Button>
                </div>
              ) : (
                <p className="text-[#4D4D4D] text-center py-8">No courses enrolled yet</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Live Class */}
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold text-navy mb-3 md:mb-4">Upcoming Live Class</h3>
              {nextClass ? (
                <div className="space-y-4">
                  <h4 className="font-semibold text-[17px] text-navy">{nextClass.title}</h4>
                  <p className="text-sm text-[#4D4D4D] font-light">{nextClass.description}</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-[#4D4D4D]">
                      <Calendar className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                      <span>{new Date(nextClass.scheduled_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-[#4D4D4D]">
                      <Clock className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                      <span>{new Date(nextClass.scheduled_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <Button 
                      onClick={() => window.open(nextClass.meeting_url, '_blank')}
                      className="flex-1 bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 rounded-xl transition-smooth btn-press"
                    >
                      Join Class
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 border-2 border-[#E7E7E7] text-navy font-semibold py-3 rounded-xl hover:border-primary-blue transition-smooth btn-press"
                    >
                      Set Reminder
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-[#4D4D4D] text-center py-8">No upcoming classes</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Your Rank */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-navy">Your Rank</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/leaderboard')}
                className="text-primary-blue hover:text-[#1874CD] font-medium text-sm"
              >
                View Leaderboard →
              </Button>
            </div>
            <div className="flex items-center justify-between p-6 rounded-2xl bg-gradient-to-br from-[#F7F7F7] to-[#E7E7E7]">
              <div className="flex items-center space-x-4">
                {/* FIXED: Show medals for rank 1, 2, 3 instead of numbers */}
                <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-white text-xl relative">
                  {userRank === 1 ? (
                    // Gold Medal for Rank 1
                    <svg width="56" height="56" viewBox="0 0 100 100" className="drop-shadow-lg">
                      <defs>
                        <linearGradient id="gold" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#FFD700" />
                          <stop offset="50%" stopColor="#FFC700" />
                          <stop offset="100%" stopColor="#DAA520" />
                        </linearGradient>
                      </defs>
                      <circle cx="50" cy="50" r="45" fill="url(#gold)" stroke="#B8860B" strokeWidth="3"/>
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#FFF5CC" strokeWidth="2" opacity="0.6"/>
                      <text x="50" y="50" textAnchor="middle" dy=".35em" fontSize="38" fontWeight="900" fill="#8B6914">1</text>
                    </svg>
                  ) : userRank === 2 ? (
                    // Silver Medal for Rank 2
                    <svg width="56" height="56" viewBox="0 0 100 100" className="drop-shadow-lg">
                      <defs>
                        <linearGradient id="silver" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#E8E8E8" />
                          <stop offset="50%" stopColor="#C0C0C0" />
                          <stop offset="100%" stopColor="#A8A8A8" />
                        </linearGradient>
                      </defs>
                      <circle cx="50" cy="50" r="45" fill="url(#silver)" stroke="#808080" strokeWidth="3"/>
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#FFFFFF" strokeWidth="2" opacity="0.6"/>
                      <text x="50" y="50" textAnchor="middle" dy=".35em" fontSize="38" fontWeight="900" fill="#505050">2</text>
                    </svg>
                  ) : userRank === 3 ? (
                    // Bronze Medal for Rank 3
                    <svg width="56" height="56" viewBox="0 0 100 100" className="drop-shadow-lg">
                      <defs>
                        <linearGradient id="bronze" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#E89C5C" />
                          <stop offset="50%" stopColor="#CD7F32" />
                          <stop offset="100%" stopColor="#A0522D" />
                        </linearGradient>
                      </defs>
                      <circle cx="50" cy="50" r="45" fill="url(#bronze)" stroke="#8B4513" strokeWidth="3"/>
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#FFDAB9" strokeWidth="2" opacity="0.6"/>
                      <text x="50" y="50" textAnchor="middle" dy=".35em" fontSize="38" fontWeight="900" fill="#5C3317">3</text>
                    </svg>
                  ) : (
                    // Regular number badge for rank 4+
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                      userRank && userRank <= 10 ? 'bg-primary-blue' : 'bg-[#A5A5A5]'
                    }`}>
                      <span className="text-xl font-bold">{userRank || '—'}</span>
                    </div>
                  )}
                </div>
                <Avatar className="w-14 h-14 border-2 border-white">
                  <AvatarImage src={getPhotoUrl(user.profile_photo)} />
                  <AvatarFallback className="text-lg font-semibold bg-primary-blue text-white">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-bold text-[17px] text-navy flex items-center space-x-2">
                    <span>{user.first_name} {user.last_name}</span>
                    <VerifiedBadge show={user.profile_verified} size="sm" />
                  </p>
                  <p className="text-sm text-[#4D4D4D]">{user.city || 'Not specified'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-black text-2xl text-navy">{user.xp_points} <span className="text-sm font-normal">GP</span></p>
                <p className="text-sm text-[#4D4D4D]">{user.weekly_streak_count || 0} week streak ({user.current_week_logins || 0}/1 day this week)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Challenges & Rewards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          
          {/* Challenges */}
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold text-navy mb-3 md:mb-4">Challenges</h3>

              {challenges.length > 0 ? (
                <div className="space-y-3">
                  {challenges.slice(0, 3).map((item) => {
                    const isCompleted = item.completion.status === 'completed';
                    const bgColor = isCompleted ? 'bg-green-50' : 'bg-blue-50';
                    
                    return (
                      <div 
                        key={item.challenge.id} 
                        className={`p-4 rounded-xl ${bgColor} border border-transparent hover:border-primary-blue transition-all duration-300 cursor-pointer`}
                        onClick={() => navigate('/challenges')}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-sm text-navy flex-1 line-clamp-1 pr-2">
                            {item.challenge.title}
                          </h4>
                          <div className="flex items-center space-x-1 flex-shrink-0">
                            <Trophy className="w-4 h-4 text-yellow-600" strokeWidth={2} />
                            <span className="text-sm font-bold text-yellow-600">{item.challenge.gp_reward}</span>
                          </div>
                        </div>
                        <p className="text-xs text-[#4D4D4D] line-clamp-2">
                          {item.challenge.description}
                        </p>
                      </div>
                    );
                  })}
                  {challenges.length > 3 && (
                    <Button
                      onClick={() => navigate('/challenges')}
                      variant="ghost"
                      size="sm"
                      className="w-full text-primary-blue hover:text-[#1874CD] text-sm font-medium"
                    >
                      View All Challenges →
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-[#4D4D4D]">No challenges assigned yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rewards */}
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold text-navy mb-3 md:mb-4">Rewards</h3>
              <div className="text-center py-8">
                <p className="text-5xl font-bold text-navy mb-2">0</p>
                <p className="text-sm font-semibold text-primary-blue">Coming Soon!</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Announcements */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-navy">Announcements</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/announcements')}
                className="text-primary-blue hover:text-[#1874CD] font-medium text-sm"
              >
                View All →
              </Button>
            </div>
            {announcements.length > 0 ? (
              <div className="space-y-2">
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="p-4 rounded-xl bg-[#F7F7F7] hover:bg-[#E7E7E7] transition-smooth cursor-pointer">
                    <p className="font-semibold text-[15px] text-navy mb-1">{announcement.title}</p>
                    <p className="text-sm text-[#4D4D4D] line-clamp-1">{announcement.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#4D4D4D] text-center py-8">No announcements</p>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Poll Popup */}
      {showPoll && <PollPopup onClose={() => setShowPoll(false)} />}
    </Layout>
  );
}